<template>
	<el-drawer
		title="智能体任务详情"
		:size="drawerWidth + 'px'"
		:visible.sync="visible"
		direction="rtl"
		:before-close="onClose"
		:append-to-body="true"
		:modal-append-to-body="true"
		:modal="false"
		:z-index="10000"
		custom-class="agent-task-detail-drawer">

		<!-- 左侧拖拽分隔条 -->
		<div class="resize-handle" @mousedown="startResize"></div>

		<div class="agent-task-detail" v-loading="loading" element-loading-text="加载中...">
			<!-- 顶部工具栏 -->
			<div class="detail-toolbar">
				<div class="toolbar-info">
					<span class="agent-name">{{ agentName }}</span>
					<span class="task-id">任务ID: {{ displayTaskId }}</span>
				</div>
				<el-button
					size="small"
					type="primary"
					icon="el-icon-link"
					@click="openInNewTab">
					在新标签页打开
				</el-button>
			</div>

			<!-- iframe容器 -->
			<div class="iframe-container" ref="iframeContainer">
				<div v-if="!hasIframe" class="no-session-tip">
					<i class="el-icon-loading"></i>
					<p>正在加载会话...</p>
				</div>
			</div>
		</div>
	</el-drawer>
</template>

<script>
const MIN_WIDTH = 400;
const MAX_WIDTH_RATIO = 0.85;
const DEFAULT_WIDTH = 520;

export default {
	name: 'AgentTaskDetailPanel',
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		taskStatusData: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			hasIframe: false,
			loading: false,
			drawerWidth: DEFAULT_WIDTH,
			isResizing: false
		};
	},
	computed: {
		agentName() {
			return this.taskStatusData?.agentName || '未知智能体';
		},
		displayTaskId() {
			const taskId = this.taskStatusData?.taskId || '';
			return taskId.length > 12 ? taskId.substring(0, 12) + '...' : taskId;
		},
		detailUrl() {
			return this.taskStatusData?.shareUrl || null;
		}
	},
	methods: {
		startResize(e) {
			e.preventDefault();
			this.isResizing = true;
			const startX = e.clientX;
			const startWidth = this.drawerWidth;
			const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;

			// 🔥 创建全屏透明遮罩，确保拖拽期间所有鼠标事件都能被捕获
			const overlay = document.createElement('div');
			overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;cursor:col-resize;';
			document.body.appendChild(overlay);

			const onMouseMove = (e) => {
				const delta = startX - e.clientX;
				this.drawerWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta));
			};

			const onMouseUp = () => {
				this.isResizing = false;
				overlay.remove();
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
			};

			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
		},

		showIframeInPanel() {
			if (!this.detailUrl) {
				this.$message.warning('任务详情链接不可用');
				return;
			}

			this.$nextTick(() => {
				const container = this.$refs.iframeContainer;
				if (container) {
					const iframe = document.createElement('iframe');
					iframe.src = this.detailUrl;
					iframe.style.width = '100%';
					iframe.style.height = '100%';
					iframe.style.border = 'none';
					container.innerHTML = '';
					container.appendChild(iframe);
					this.hasIframe = true;
				}
			});
		},

		hideIframeFromPanel() {
			if (!this.hasIframe) return;
			const container = this.$refs.iframeContainer;
			if (container) {
				container.innerHTML = '<div class="no-session-tip"><i class="el-icon-loading"></i><p>正在加载会话...</p></div>';
			}
			this.hasIframe = false;
		},

		onClose() {
			this.hideIframeFromPanel();
			this.$emit('update:visible', false);
		},

		openInNewTab() {
			if (this.detailUrl) {
				window.open(this.detailUrl, '_blank');
			} else {
				this.$message.warning('任务详情链接不可用');
			}
		}
	},
	watch: {
		visible(newVal) {
			if (newVal) {
				this.$nextTick(() => this.showIframeInPanel());
			} else {
				this.hideIframeFromPanel();
			}
		}
	},
	beforeDestroy() {
		this.hideIframeFromPanel();
	}
}
</script>

<style lang="scss" scoped>
.resize-handle {
	position: absolute;
	left: 0;
	top: 0;
	width: 6px;
	height: 100%;
	cursor: col-resize;
	z-index: 10;
	background: transparent;
	transition: background 0.2s;

	&:hover,
	&:active {
		background: #409eff;
	}
}

.agent-task-detail {
	display: flex;
	flex-direction: column;
	height: 100%;

	.detail-toolbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px;
		border-bottom: 1px solid #ebeef5;
		background: #fff;

		.toolbar-info {
			display: flex;
			flex-direction: column;
			gap: 4px;

			.agent-name {
				font-size: 16px;
				font-weight: 500;
				color: #303133;
			}

			.task-id {
				font-size: 12px;
				color: #909399;
				font-family: monospace;
			}
		}
	}

	.iframe-container {
		flex: 1;
		position: relative;
		background: #fff;

		iframe {
			width: 100% !important;
			height: 100% !important;
			border: none !important;
		}

		.no-session-tip {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			height: 100%;
			color: #909399;

			i {
				font-size: 48px;
				margin-bottom: 16px;
			}

			p {
				font-size: 14px;
			}
		}
	}
}
</style>

<style lang="scss">
.agent-task-detail-drawer {
	.el-drawer__header {
		padding: 16px 20px;
		margin-bottom: 0;
		border-bottom: 1px solid #ebeef5;
	}

	.el-drawer__body {
		padding: 0;
		overflow: hidden;
	}
}
</style>
